import type { PowerCoolingLine } from './powerCoolingAnswers';
import type {
  ScreeningArchitectureRecommendations,
  ScreeningRecommendationSetV1,
} from './screeningArchitectureRecommendations';

export interface RecommendationFirstBriefDiagnostic {
  state: string;
  reason: string;
  nextStep: string;
}

export interface RecommendationFirstBriefOptions {
  recommendationSet?: ScreeningRecommendationSetV1 | null;
  sourceDiagnostic?: RecommendationFirstBriefDiagnostic | null;
}

/** UI projection after the engineering/recorded-input model is built. Advice
 * comes only from the host's already validated current screening snapshot;
 * this never changes the inputs used to calculate risks or promoted results. */
export function recommendationFirstBrief(
  recordedLines: readonly PowerCoolingLine[],
  advice: ScreeningArchitectureRecommendations,
  options?: RecommendationFirstBriefOptions,
): { lines: PowerCoolingLine[]; recordedInputs: Map<string, PowerCoolingLine> } {
  return {
    recordedInputs: new Map(recordedLines.filter(line => line.basis === 'recorded').map(line => [line.id, line])),
    lines: recordedLines.map(original => {
      const saved = advice[original.id];
      const label = original.id === 'dc-power' ? 'DC size range' : original.label;
      if (saved) {
        return {
          id: original.id, label, value: saved.value, basis: 'recommended',
          basisNote: saved.basisNote, spoken: saved.spoken,
          evidenceRefs: [{ kind: 'report', reportId: saved.reportId }],
        };
      }
      const question = options?.recommendationSet?.questions[original.id];
      const sourceDiag = options?.sourceDiagnostic;
      const basisNote = question?.status === 'unresolved' && question.unresolvedReason
        ? `No current saved screening recommendation is available for this question — ${question.unresolvedReason}`
        : question?.status === 'withheld-source'
          ? 'No current saved screening recommendation is available for this question (saved recommendation withheld because a cited source is unavailable).'
          : sourceDiag && sourceDiag.state !== 'ready'
            ? sourceDiag.reason
            : 'No current saved screening recommendation is available for this question.';
      const nextStep = question?.status === 'unresolved' && question.nextStep
        ? question.nextStep
        : question?.status === 'withheld-source' && question.nextStep
          ? question.nextStep
          : sourceDiag && sourceDiag.state !== 'ready'
            ? sourceDiag.nextStep
            : 'Review or refresh Screening Assessment. Recorded inputs remain comparison constraints, not a recommended approach.';
      return {
        id: original.id, label, value: 'Not yet determined', basis: 'not-determined',
        basisNote,
        openIn: 'early-screening',
        nextStep,
        route: { kind: 'phase', phase: 'build' }, evidenceRefs: [],
      };
    }),
  };
}
